import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import { TPurchaseOrderItem } from 'utils/types';

type RemarksCellProps = {
  item: TPurchaseOrderItem;
  branchDomain?: string;
  purchaseOrderId?: string;
  canEdit: boolean;
  onUpdated: (item: TPurchaseOrderItem) => void;
};

const RemarksCell = ({
  item,
  branchDomain,
  purchaseOrderId,
  canEdit,
  onUpdated
}: RemarksCellProps) => {
  const [value, setValue] = useState(item.remarks ?? '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(item.remarks ?? '');
  }, [item.remarks]);

  if (!canEdit) {
    return (
      <span className="fs-9" title={item.remarks || undefined}>
        {item.remarks || <span className="text-body-tertiary">—</span>}
      </span>
    );
  }

  const save = async () => {
    if (!branchDomain || !purchaseOrderId) {
      return;
    }

    const next = value.trim();
    const current = (item.remarks ?? '').trim();
    if (next === current) {
      return;
    }

    setIsSaving(true);
    try {
      const res = await axiosInstance.put<TPurchaseOrderItem>(
        `/api/v1/${branchDomain}/purchaseOrders/${purchaseOrderId}/items/${item.purchaseOrderItemId}/remarks`,
        { remarks: next || null }
      );
      onUpdated({
        ...item,
        ...res.data,
        remarks: res.data.remarks ?? next
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to save comment',
          { ...toastConfig, autoClose: 3000 }
        );
      }
      setValue(item.remarks ?? '');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form.Control
      as="textarea"
      rows={2}
      size="sm"
      value={value}
      disabled={isSaving}
      placeholder="Add a comment"
      onChange={e => setValue(e.target.value)}
      onBlur={() => void save()}
      style={{ minWidth: 160 }}
    />
  );
};

export default RemarksCell;
